import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { type Sample } from '@/features/samples/services/sampleSearchService';
import { type Locality } from '@/features/alojamientos/hooks/useLocalities';
import { useSampleTypes } from '@/features/samples/hooks/useSampleTypes';
import { getDecodedToken } from '@/features/auth/utils/auth';
import { useSampleRecordStatuses } from '@/features/samples/hooks/useSampleRecordStatuses';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileDown, Loader2 } from "lucide-react";
import { formatDateSpanish } from '@/lib/utils';
import { SampleUserType } from '@/features/create-samples/types/sample.types';
import { SamplingBlockReason } from '@/features/samples/services/samplingBlockReasonService';
import { TablePagination } from '../daily-samples/TablePagination';
import { SampleRow } from './SampleRow';
import { useRoleAccess } from '@/features/auth/hooks/useRoleAccess';
import { ROLES } from '@/features/auth/constants/roles';
import { useSampleExport } from '@/features/samples/hooks/useSampleExport';

interface DailySamplesTableProps {
  samples?: Sample[];
  localities?: Locality[];
  isLoading?: boolean;
  selectedSampleType?: number;
  sampleUserTypes?: SampleUserType[];
  samplingBlockReasons?: SamplingBlockReason[];
}

export const DailySamplesTable = ({ 
  samples = [], 
  localities = [],
  isLoading = false,
  selectedSampleType,
  sampleUserTypes,
  samplingBlockReasons
}: DailySamplesTableProps) => {
  const { sampleTypes } = useSampleTypes();
  const { sampleRecordStatuses } = useSampleRecordStatuses();
  const { hasRole } = useRoleAccess();
  const { exportToExcel, isExporting } = useSampleExport();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const canDownloadExcel = hasRole([ROLES.ADMIN, ROLES.OBSERVATORIO_EDITOR]);

  const handleExportExcel = () => {
    const rawDate = samples[0]?.sampleDate;
    exportToExcel({
      sampleDate: rawDate ? format(new Date(rawDate), 'yyyy-MM-dd') : undefined,
      sampleTypeId: selectedSampleType ?? undefined,
    });
  };

  // Obtener información del usuario actual
  const decodedToken = getDecodedToken();
  const userName = [decodedToken?.firstName, decodedToken?.lastName].filter(Boolean).join(' ') || 'Usuario';

  // Obtener el tipo de muestra seleccionado
  const selectedSampleTypeInfo = sampleTypes.find(type => type.id === selectedSampleType);
  const sampleTypeName = selectedSampleTypeInfo?.name || 'Muestra';

  // Variables para mostrar columnas especiales solo si hay título
  const showSpecialDate1 = !!sampleUserTypes?.find(type => type.titleSpecialDate1);
  const showSpecialDate2 = !!sampleUserTypes?.find(type => type.titleSpecialDate2);
  const showSpecialDate3 = !!sampleUserTypes?.find(type => type.titleSpecialDate3);
  const showSpecialDate4 = !!sampleUserTypes?.find(type => type.titleSpecialDate4);
  const specialDate1Title = sampleUserTypes?.find(type => type.titleSpecialDate1)?.titleSpecialDate1;
  const specialDate2Title = sampleUserTypes?.find(type => type.titleSpecialDate2)?.titleSpecialDate2;
  const specialDate3Title = sampleUserTypes?.find(type => type.titleSpecialDate3)?.titleSpecialDate3;
  const specialDate4Title = sampleUserTypes?.find(type => type.titleSpecialDate4)?.titleSpecialDate4;


  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Resetear a la primera página cuando cambia el número de items por página
  };

  // Ordenar todos los elementos antes de la paginación
  const sortedSamples = [...samples].sort((a, b) => {
    // Primero ordenar por fecha
    const dateA = a.sampleDate ? new Date(a.sampleDate).getTime() : 0;
    const dateB = b.sampleDate ? new Date(b.sampleDate).getTime() : 0;
    
    if (dateA !== dateB) {
      return dateB - dateA; // Orden descendente por fecha
    }
    
    // Si las fechas son iguales, ordenar por nombre del establecimiento
    const nameA = a.accommodation.establishmentName?.toLowerCase() || '';
    const nameB = b.accommodation.establishmentName?.toLowerCase() || '';
    return nameA.localeCompare(nameB);
  });

  // Calcular los índices para la paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedSamples.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedSamples.length / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (!samples.length) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No se encontraron muestras
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3">
            <span>Muestra {sampleTypeName} - {userName}</span>
            {samples[0]?.sampleDate && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-sm px-3 py-1">
                {formatDateSpanish(samples[0].sampleDate)}
              </Badge>
            )}
          </CardTitle>
          {canDownloadExcel && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              disabled={isExporting || samples.length === 0}
            >
              {isExporting
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <FileDown className="h-4 w-4 mr-2" />
              }
              Descargar Excel
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Table className="border border-gray-200 mb-4">
          <TableHeader className="bg-gray-100">
            <TableRow>
              <TableHead className="sticky left-0 bg-gray-100 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">Establecimiento</TableHead>
              <TableHead>Localidad</TableHead>
              <TableHead>Telefono</TableHead>
              <TableHead>Whatsapp</TableHead>
              <TableHead>Pax Anoche</TableHead>
              <TableHead>Hab. Ocupadas</TableHead>
              
              {showSpecialDate1 && (
                <TableHead>{specialDate1Title}</TableHead>
              )}
              {showSpecialDate2 && (
                <TableHead>{specialDate2Title}</TableHead>
              )}
              {showSpecialDate3 && (
                <TableHead>{specialDate3Title}</TableHead>
              )}
              {showSpecialDate4 && (
                <TableHead>{specialDate4Title}</TableHead>
              )}
              <TableHead style={{ display: 'none' }}>Observaciones</TableHead>
              <TableHead>Observaciones</TableHead>
              <TableHead>Estado Llamada</TableHead>
              <TableHead className="w-[200px]">Estado</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentItems.map((sample) => (
              <SampleRow
                key={sample.id}
                sample={sample}
                localities={localities}
                sampleUserTypes={sampleUserTypes}
                samplingBlockReasons={samplingBlockReasons}
                sampleRecordStatuses={sampleRecordStatuses}
                showSpecialDate1={showSpecialDate1}
                showSpecialDate2={showSpecialDate2}
                showSpecialDate3={showSpecialDate3}
                showSpecialDate4={showSpecialDate4}
                specialDate1Title={specialDate1Title}
                specialDate2Title={specialDate2Title}
                specialDate3Title={specialDate3Title}
                specialDate4Title={specialDate4Title}
              />
            ))}
          </TableBody>
        </Table>

      <TablePagination 
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        totalItems={samples.length}
      />
    </CardContent>
    </Card>
  );
};
